import React, { useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import {
  AreaChart,
  Area,
  Cell,
  Pie,
  PieChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  ClipboardList,
  ExternalLink,
  Eye,
  Flame,
  ImageOff,
  MessageSquareText,
  Plus,
  ShoppingCart,
  Star,
  TrendingDown,
  TrendingUp,
  Users,
  UtensilsCrossed,
  Wifi,
  Receipt,
  PackageSearch,
  Clock,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Tooltip as UiTooltip } from '@/components/ui/tooltip';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { getZoneFirstData, zoneFirstDataKey, type DataSection } from '@/api/features';
import { Popover } from '@/components/ui/popover';
import { ImageLightbox } from '@/components/ui/image-lightbox';
import { useCurrentZone } from '@/hooks/useCurrentZone';
import { useT } from '@/contexts/I18nContext';
import { BranchRequiredState } from '@/components/common/BranchRequiredState';
import { DataErrorState } from '@/components/common/DataErrorState';
import { MobileSubHeader } from '@/components/layout/MobileSubHeader';
import { usePageTitle, usePageHeaderSlots } from '@/contexts/PageHeaderContext';
import { DateRangePicker } from '@/components/ui/date-picker';
import { getZoneAnalyticsTab, type ChronicFailItem, type CleanupHistogram, type TaskReporterStat, type TaskTimingHistogram } from '@/api/analytics';
import { getTablesSummary } from '@/api/dashboardSummary';
import { listOpsTasks } from '@/api/opsTasks';
import AnalyticsConversationsTab from './AnalyticsConversationsTab';
import AnalyticsLayoutDetailTab from './AnalyticsLayoutDetail';
import AnalyticsQueueDetailTab from './AnalyticsQueueDetail';
import AnalyticsVisitsTab from './AnalyticsVisitsTab';
import AnalyticsReviewsTab from './AnalyticsReviewsTab';
import AnalyticsReceiptsTab from './AnalyticsReceiptsTab';
import AnalyticsItemSalesTab from './AnalyticsItemSalesTab';
import OrderPlanTab from '@/components/orderPlan/OrderPlanTab';
import AnalyticsWifiTab from './AnalyticsWifiTab';
import { useZoneFeatures } from '@/hooks/useZoneFeatures';
import HeatmapTab from '@/pages/Usage/HeatmapPage';
import { TableStateStrips } from '@/components/report/TableStateStrips';
import { useSearchParams } from 'react-router-dom';
import { useRememberFilters } from '@/hooks/useRememberFilters';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';
import { bucketsSpanDays, fmtBucketTick, fmtPeriod } from '@/components/report/day/timeAxis';

type Granularity = 'hour' | 'day' | 'week';

function isoOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function todayIso(): string {
  return isoOf(new Date());
}
const pad2 = (n: number) => String(n).padStart(2, '0');

/** ?time=HHMM-HHMM — a sub-day window picked by dragging over the выкладка
 *  chart: starts at HH:MM of `from`, ends at HH:MM of `to` (local time). */
function parseTimeWindow(from: string, to: string, raw: string | null): [number, number] | null {
  const m = raw?.match(/^(\d{2})(\d{2})-(\d{2})(\d{2})$/);
  if (!m) return null;
  const lo = new Date(`${from}T${m[1]}:${m[2]}:00`).getTime();
  const hi = new Date(`${to}T${m[3]}:${m[4]}:${m[3] === '23' && m[4] === '59' ? '59' : '00'}`).getTime();
  return Number.isFinite(lo) && Number.isFinite(hi) && hi > lo ? [lo, hi] : null;
}

function daysAgoIso(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return isoOf(d);
}

function fmtDuration(s: number | null): string {
  if (s == null) return '—';
  const mins = Math.floor(s / 60);
  const secs = Math.round(s % 60);
  if (mins === 0) return `${secs} с`;
  if (secs === 0) return `${mins} мин`;
  return `${mins} мин ${secs} с`;
}

// ── Delta badge ───────────────────────────────────────────────────────────────

interface DeltaProps {
  delta: number | null;
  higherIsBetter?: boolean;
  unit?: string;
}

function Delta({ delta, higherIsBetter = true, unit = '' }: DeltaProps) {
  if (delta === null || delta === 0) return <span className="text-muted-foreground text-xs">—</span>;
  const positive = delta > 0;
  const good = higherIsBetter ? positive : !positive;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 text-xs font-medium',
        good ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'
      )}
    >
      {positive ? <TrendingUp className="size-3.5" /> : <TrendingDown className="size-3.5" />}
      {positive ? '+' : ''}
      {delta}
      {unit}
    </span>
  );
}

// ── KPI chip ──────────────────────────────────────────────────────────────────

interface KpiChipProps {
  label: string;
  value: string;
  delta?: number | null;
  deltaUnit?: string;
  higherIsBetter?: boolean;
  accent?: boolean;
}

function KpiChip({ label, value, delta, deltaUnit = '', higherIsBetter = true, accent = false }: KpiChipProps) {
  // ring-INSET: an outset ring on the left-most chip got clipped by an
  // overflow-hidden ancestor («Обычно убираем за» border cut, Alexey 2026-07-21).
  return (
    <Card className={cn('flex-1 min-w-0', accent && 'ring-1 ring-inset ring-primary/20')}>
      <CardContent className="pt-3 pb-3 px-4">
        <div className="text-[11px] text-muted-foreground mb-0.5 truncate">{label}</div>
        <div className="text-2xl font-bold text-foreground leading-tight">{value}</div>
        {delta !== undefined && (
          <div className="mt-1">
            <Delta delta={delta ?? null} higherIsBetter={higherIsBetter} unit={deltaUnit} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── KPI row ───────────────────────────────────────────────────────────────────

function KpiRow({ children }: { children: React.ReactNode }) {
  return <div className="flex gap-3 flex-wrap">{children}</div>;
}

// ── Grafana button ────────────────────────────────────────────────────────────

function GrafanaButton({ url, label }: { url: string; label: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors"
    >
      <ExternalLink className="size-3.5" />
      {label}
    </a>
  );
}

// ── Sparkline chart ───────────────────────────────────────────────────────────

interface SparklineProps {
  data: { date: string; value: number }[];
  granularity?: Granularity;
  color?: string;
  label: string;
  unit?: string;
  textSummary?: string;
}

function Sparkline({ data, granularity = 'day', color = '#6366f1', label, unit = '', textSummary }: SparklineProps) {
  // Shared bucket labels (timeAxis.ts): hour buckets over several days carry
  // the date («20.10 14:00»), one day — «14:00»; day/week «01.05» (#2646).
  const multiDay = granularity === 'hour' && bucketsSpanDays(data.map((p) => p.date));
  const formatDate = (d: string) => fmtBucketTick(d, multiDay);

  if (data.length < 2) {
    return (
      <Card>
        <CardContent className="flex h-44 items-center justify-center flex-col gap-1 text-center px-6">
          {data.length === 1 && textSummary ? (
            <>
              <div className="text-base font-semibold text-foreground">{textSummary}</div>
              <div className="text-xs text-muted-foreground mt-1">{label}</div>
            </>
          ) : (
            <div className="text-sm text-muted-foreground">{label}</div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-3 pb-2 px-3 [&_.recharts-wrapper]:outline-none [&_.recharts-wrapper:focus]:outline-none [&_.recharts-surface]:outline-none [&_.recharts-surface:focus]:outline-none">
        <div className="text-xs text-muted-foreground mb-2">{label}</div>
        <ResponsiveContainer width="100%" height={160}>
          <AreaChart
            accessibilityLayer={false} data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id={`grad-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.25} />
                <stop offset="95%" stopColor={color} stopOpacity={0.0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" strokeOpacity={0.4} />
            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
              axisLine={false}
              tickLine={false}
              // 40 (vs the old 32) so "100%" doesn't get clipped to "00%".
              width={40}
              tickFormatter={(v) => `${v}${unit}`}
            />
            <Tooltip
            isAnimationActive={false}
              contentStyle={{
                fontSize: 12,
                borderRadius: 8,
                border: '1px solid var(--color-border)',
                background: 'var(--color-card)',
                color: 'var(--color-foreground)',
              }}
              formatter={(v) => [`${v ?? ''}${unit}`, '']}
              labelFormatter={(d) => formatDate(String(d))}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={2}
              fill={`url(#grad-${color.replace('#', '')})`}
              dot={false}
              activeDot={{ r: 4 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

// ── Cleanup histogram ─────────────────────────────────────────────────────────

function CleanupHistogramBar({ hist, label }: { hist: CleanupHistogram; label: string }) {
  const buckets = [
    { key: 'le_2m', label: '≤ 2 мин', color: 'bg-green-500', count: hist.le_2m },
    { key: '2_5m', label: '2–5 мин', color: 'bg-yellow-400', count: hist['2_5m'] },
    { key: '5_15m', label: '5–15 мин', color: 'bg-orange-400', count: hist['5_15m'] },
    { key: 'gt_15m', label: '> 15 мин', color: 'bg-red-500', count: hist.gt_15m },
  ];
  const total = buckets.reduce((s, b) => s + b.count, 0);
  if (total === 0) return null;
  const active = buckets.filter((b) => b.count > 0);

  return (
    <Card>
      <CardContent className="pt-3 pb-3 px-3">
        <div className="text-xs text-muted-foreground mb-2">{label}</div>
        <div className="flex h-5 rounded overflow-hidden gap-px">
          {active.map((b) => (
            <UiTooltip key={b.key} content={`${b.label}: ${b.count}`}><div
              className={cn(b.color, 'flex items-center justify-center text-[10px] text-white font-medium transition-all')}
              style={{ width: `${(b.count / total) * 100}%` }}
            >
              {(b.count / total) > 0.08 ? b.count : ''}
            </div></UiTooltip>
          ))}
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
          {buckets.map((b) => (
            <div key={b.key} className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <div className={cn('w-2 h-2 rounded-sm shrink-0', b.color)} />
              {b.label}: <span className="font-medium text-foreground">{b.count}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Task timing histogram ─────────────────────────────────────────────────────
//
// 4-bucket distribution of done OpsTasks against their deadline. Same visual
// idiom as CleanupHistogramBar so the page stays consistent. Buckets and
// thresholds match the backend: ≥30 min before / within 30 min / 30 min–2 h
// after / > 2 h after.

// Colours per Alexey 2026-07-21: late <2 h = YELLOW, late >2 h = ORANGE,
// «не сделано» = RED. `hist` is null when nothing was done — the не-сделано
// segment must still render, so it degrades to zeros.
function TimingHistogramBar({ hist, notDone, label }: { hist: TaskTimingHistogram | null; notDone: number; label: string }) {
  const buckets = [
    { key: 'early', label: 'Заранее', color: 'bg-green-500', count: hist?.early ?? 0 },
    { key: 'on_time', label: 'В срок', color: 'bg-emerald-400', count: hist?.on_time ?? 0 },
    { key: 'late_short', label: 'Опоздание < 2 ч', color: 'bg-yellow-400', count: hist?.late_short ?? 0 },
    { key: 'late_long', label: 'Опоздание > 2 ч', color: 'bg-orange-400', count: hist?.late_long ?? 0 },
    { key: 'not_done', label: 'Не сделано', color: 'bg-red-500', count: notDone },
  ];
  const total = buckets.reduce((s, b) => s + b.count, 0);
  if (total === 0) return null;
  const active = buckets.filter((b) => b.count > 0);

  return (
    <Card>
      <CardContent className="pt-3 pb-3 px-3">
        <div className="text-xs text-muted-foreground mb-2">{label}</div>
        <div className="flex h-5 rounded overflow-hidden gap-px">
          {active.map((b) => (
            <UiTooltip key={b.key} content={`${b.label}: ${b.count}`}><div
              className={cn(b.color, 'flex items-center justify-center text-[10px] text-white font-medium transition-all')}
              style={{ width: `${(b.count / total) * 100}%` }}
            >
              {(b.count / total) > 0.08 ? b.count : ''}
            </div></UiTooltip>
          ))}
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
          {buckets.map((b) => (
            <div key={b.key} className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <div className={cn('w-2 h-2 rounded-sm shrink-0', b.color)} />
              {b.label}: <span className="font-medium text-foreground">{b.count}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Checklist digest section (chronic-fail + often-completed) ────────────────

function failRateClass(rate: number): string {
  if (rate < 30) return 'text-red-500';
  if (rate < 50) return 'text-orange-500';
  return 'text-yellow-600 dark:text-yellow-400';
}

function doneRateClass(_rate: number): string {
  return 'text-green-600 dark:text-green-400';
}

function ChecklistDigestSection({
  items,
  title,
  empty,
  icon: Icon,
  iconClassName,
  rateClass,
  zoneId,
  from,
  to,
}: {
  items: ChronicFailItem[];
  title: string;
  empty: string;
  icon: LucideIcon;
  iconClassName: string;
  rateClass: (rate: number) => string;
  zoneId: string;
  from: string;
  to: string;
}) {
  const { t } = useT();
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);

  // Disambiguate same-name items by appending due_time. ChecklistItem names
  // can repeat across times of day ("Приготовить круассаны" 08:00 vs 16:00) —
  // showing them as identical rows is confusing. We only suffix when there's
  // an actual collision so unambiguous rows stay clean.
  const nameCounts = new Map<string, number>();
  for (const it of items) {
    nameCounts.set(it.task_text, (nameCounts.get(it.task_text) ?? 0) + 1);
  }
  const labelFor = (it: ChronicFailItem) =>
    (nameCounts.get(it.task_text) ?? 0) > 1 && it.due_time
      ? t('analytics.tasks.taskAt', { text: it.task_text, time: it.due_time })
      : it.task_text;

  // Default view shows the top DIGEST_LIMIT entries; the rest hide behind a
  // "see all" toggle. Backend sorts significant rows (total >= 2) ahead of
  // 1-shots, so the default cut surfaces the meaningful patterns first and
  // 1-shots only fill empty slots when there isn't enough signal otherwise.
  const DIGEST_LIMIT = 5;
  const visible = showAll ? items : items.slice(0, DIGEST_LIMIT);
  const hiddenCount = items.length - visible.length;

  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2">
        <Icon className={cn('size-3.5 shrink-0', iconClassName)} />
        <span className="text-sm font-semibold text-foreground">{title}</span>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-muted-foreground">{empty}</div>
      ) : (
        <div className="space-y-1.5">
          {visible.map((item) => {
            const isOpen = expandedId === item.checklist_item_id;
            return (
              <div
                key={item.checklist_item_id}
                className="rounded-lg border border-border overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setExpandedId(isOpen ? null : item.checklist_item_id)}
                  className={cn(
                    'flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors',
                    'hover:bg-muted/40',
                    isOpen && 'bg-muted/40',
                  )}
                  aria-expanded={isOpen}
                >
                  <span className="flex min-w-0 items-center gap-1.5">
                    {isOpen
                      ? <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
                      : <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />}
                    <span className="text-foreground truncate">{labelFor(item)}</span>
                  </span>
                  <span
                    className={cn(
                      'shrink-0 text-xs font-semibold tabular-nums',
                      rateClass(item.completion_rate),
                    )}
                  >
                    {item.done} / {item.total} · {item.completion_rate}%
                  </span>
                </button>
                {isOpen && (
                  <ChecklistItemReports
                    zoneId={zoneId}
                    checklistItemIds={item.checklist_item_ids}
                    from={from}
                    to={to}
                  />
                )}
              </div>
            );
          })}
          {(hiddenCount > 0 || showAll) && items.length > DIGEST_LIMIT && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="mt-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {showAll
                ? t('analytics.tasks.collapse')
                : t('analytics.tasks.showAll', { n: hiddenCount })}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Drill-in panel: every OpsTask execution for the digest row's
// ChecklistItem(s) within the analytics period. Each task is its own
// collapsible block — header (deadline + report time + status) is always
// visible; body (text + photos) toggles. With 20+ executions on a typical
// merged-weekday row the panel would otherwise scroll forever, so we
// default to all-collapsed and offer bulk expand/collapse.
export function ChecklistItemReports({
  zoneId,
  checklistItemIds,
  from,
  to,
}: {
  zoneId: string;
  checklistItemIds: number[];
  from: string;
  to: string;
}) {
  const { t } = useT();
  // Stable cache key across renders even though checklistItemIds is a fresh
  // array reference each time the parent renders.
  const idsKey = checklistItemIds.join(',');
  const { data, isLoading } = useSWR(
    ['analytics:checklist-reports', zoneId, idsKey, from, to],
    () => listOpsTasks({ zoneId, checklistItemIds, from, to, pageSize: 200 }).then((r) => r.data),
    // No focus-revalidation: a refetch on window refocus re-renders the whole
    // analytics tab and makes the chart/photo panels "пляшут" (Alexey 2026-07-21).
    { revalidateOnFocus: false },
  );

  const tasks = data?.results ?? [];
  // Default state: collapse everything when there are many entries (the
  // common case after weekday-merging), keep them open when there are only
  // a couple so the user doesn't have to click for the obvious case.
  const COLLAPSE_DEFAULT_THRESHOLD = 4;
  const [expandedIds, setExpandedIds] = useState<Set<number | string> | null>(null);
  const visibleSet = expandedIds ?? (
    tasks.length < COLLAPSE_DEFAULT_THRESHOLD
      ? new Set(tasks.map((tk) => tk.id))
      : new Set<number | string>()
  );
  const allOpen = tasks.length > 0 && tasks.every((tk) => visibleSet.has(tk.id));

  const toggleOne = (id: number | string) => {
    const next = new Set(visibleSet);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExpandedIds(next);
  };
  const expandAll = () => setExpandedIds(new Set(tasks.map((tk) => tk.id)));
  const collapseAll = () => setExpandedIds(new Set());

  if (isLoading) {
    return (
      <div className="border-t border-border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
        {t('analytics.tasks.expand.loading')}
      </div>
    );
  }
  if (!tasks.length) {
    return (
      <div className="border-t border-border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
        {t('analytics.tasks.expand.empty')}
      </div>
    );
  }

  return (
    <div className="border-t border-border bg-muted/20 px-3 py-2 space-y-2">
      {tasks.length > 1 && (
        <div className="flex items-center justify-between text-[10px] text-muted-foreground">
          <span>{t('analytics.tasks.expand.count', { n: tasks.length })}</span>
          <button
            type="button"
            onClick={allOpen ? collapseAll : expandAll}
            className="font-medium hover:text-foreground transition-colors"
          >
            {allOpen
              ? t('analytics.tasks.expand.collapseAll')
              : t('analytics.tasks.expand.expandAll')}
          </button>
        </div>
      )}
      {tasks.map((tk) => {
        const isOpen = visibleSet.has(tk.id);
        const photos = (tk.latest_report?.photos ?? []).filter((p) => p.image_url);
        // No per-photo caption: every photo here belongs to the same task,
        // and the entry header above already shows the date + report time.
        const photoItems: ImageItem[] = photos.map((p) => ({
          id: p.id,
          name: '',
          image_url: p.image_url,
        }));
        const dueAt = tk.due_at ? new Date(tk.due_at) : null;
        const reportAt = tk.latest_report?.created_at
          ? new Date(tk.latest_report.created_at)
          : null;
        const hasBody = !!(tk.latest_report?.text || photoItems.length > 0);
        return (
          <div key={tk.id} className="text-xs">
            <button
              type="button"
              onClick={() => hasBody && toggleOne(tk.id)}
              disabled={!hasBody}
              className={cn(
                'flex w-full items-center justify-between gap-2 text-[11px] text-muted-foreground',
                hasBody && 'cursor-pointer hover:text-foreground transition-colors',
                !hasBody && 'cursor-default',
              )}
              aria-expanded={hasBody ? isOpen : undefined}
            >
              <span className="flex min-w-0 items-center gap-1.5">
                {hasBody && (
                  isOpen
                    ? <ChevronDown className="size-3 shrink-0" />
                    : <ChevronRight className="size-3 shrink-0" />
                )}
                <span className="truncate">
                  {dueAt ? dueAt.toLocaleString() : '—'}
                  {reportAt && (
                    <>
                      {' · '}
                      {t('analytics.tasks.expand.reportAt', {
                        time: reportAt.toLocaleString(),
                      })}
                    </>
                  )}
                </span>
              </span>
              <span className="shrink-0">{t(`analytics.tasks.expand.status.${tk.status}`)}</span>
            </button>
            {isOpen && hasBody && (
              <div className="mt-1 space-y-1 pl-4">
                {tk.latest_report?.text && (
                  <div className="whitespace-pre-wrap text-foreground">
                    {tk.latest_report.text}
                  </div>
                )}
                {photoItems.length > 0 && (
                  <ImageStrip items={photoItems} label="" />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Image strip ───────────────────────────────────────────────────────────────

interface ImageItem {
  id: number | string;
  name: string;
  image_url: string | null;
}

// ImageStrip — respects each image's natural aspect ratio so vertical
// rack/queue crops don't get squished into 16:9 letterboxing and small
// crops don't get up-scaled to fill a too-large card. Two layouts:
//
//   single (items.length === 1): centered card sized to the image's
//   natural ratio, capped at max-h-72 / max-w-md so a tall portrait crop
//   stays readable without flooding the screen.
//
//   strip (items.length > 1): horizontal scroller of fixed-height (h-24)
//   thumbnails, each at its own natural width. This lets portrait and
//   landscape crops live side by side without forcing a single ratio.
function ImageStrip({ items, label }: { items: ImageItem[]; label: string }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  if (!items.length) return null;

  const withImages = items.filter((it): it is ImageItem & { image_url: string } => !!it.image_url);
  const srcs = withImages.map((it) => it.image_url);
  const single = items.length === 1;

  return (
    <div>
      {label && <div className="text-xs text-muted-foreground mb-2">{label}</div>}
      {single ? (
        // Hero card — natural aspect, no forced cropping.
        <div className="inline-block max-w-md">
          {(() => {
            const item = items[0];
            const lightboxIdx = item.image_url ? 0 : -1;
            return item.image_url ? (
              <>
                <button
                  type="button"
                  onClick={() => setOpenIndex(lightboxIdx)}
                  className="block rounded-lg overflow-hidden bg-muted"
                  aria-label={item.name}
                >
                  <img
                    src={item.image_url}
                    alt={item.name}
                    className="block max-h-72 max-w-full cursor-zoom-in transition hover:opacity-90"
                    loading="lazy"
                  />
                </button>
                {item.name && (
                  <div className="mt-1 text-xs text-muted-foreground truncate">{item.name}</div>
                )}
              </>
            ) : (
              <div className="flex h-32 w-32 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                <ImageOff className="size-5" />
              </div>
            );
          })()}
        </div>
      ) : (
        // Strip — fixed height, natural width per thumbnail.
        <div className="flex gap-2 overflow-x-auto pb-1">
          {items.map((item) => {
            const lightboxIdx = item.image_url
              ? withImages.findIndex((w) => w.id === item.id)
              : -1;
            return (
              <div key={item.id} className="shrink-0">
                <div className="h-24 rounded-lg overflow-hidden bg-muted flex items-center justify-center">
                  {item.image_url ? (
                    <button
                      type="button"
                      onClick={() => setOpenIndex(lightboxIdx)}
                      aria-label={item.name}
                      className="block h-full"
                    >
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="block h-full w-auto cursor-zoom-in transition hover:opacity-90"
                        loading="lazy"
                      />
                    </button>
                  ) : (
                    <div className="flex h-full w-24 items-center justify-center text-muted-foreground">
                      <ImageOff className="size-5" />
                    </div>
                  )}
                </div>
                {item.name && (
                  <div className="mt-1 max-w-32 text-[10px] text-muted-foreground truncate">{item.name}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {openIndex != null && (
        <ImageLightbox
          srcs={srcs}
          index={openIndex}
          alt={withImages[openIndex]?.name}
          onClose={() => setOpenIndex(null)}
          onNavigate={setOpenIndex}
        />
      )}
    </div>
  );
}

// ── Skeleton ─────────────────────────────────────────────────────────────────

function SkeletonRow() {
  return (
    <div className="flex gap-3">
      {[0, 1, 2].map((i) => (
        <Card key={i} className="flex-1">
          <CardContent className="pt-3 pb-3 px-4">
            <div className="h-3 w-16 rounded bg-muted animate-pulse mb-2" />
            <div className="h-7 w-12 rounded bg-muted animate-pulse mb-1" />
            <div className="h-3 w-10 rounded bg-muted animate-pulse" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function TabLoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="h-5 w-40 rounded bg-muted animate-pulse" />
      <SkeletonRow />
      <div className="h-32 rounded bg-muted animate-pulse" />
    </div>
  );
}

/** Kept as a thin alias so every tab keeps the same call shape; the visual is
 *  the shared full-height DataErrorState (Alexey 09.09; since 26.10 #2635 it
 *  tells offline / server updating / unclear apart from the request error). */
function TabErrorCard({ error }: { error?: unknown }) {
  return <DataErrorState error={error} />;
}

// ── Queue tab ─────────────────────────────────────────────────────────────────
// Per-zone queue deep-inspection lives in AnalyticsQueueDetail.tsx (overlay
// with kitchen/cashier/waiting lines, length × minutes histogram, photos,
// wait-time, worker-per-client matrix, decrease-speed bars).

// ── Tables tab ────────────────────────────────────────────────────────────────

function TablesTab({ zoneId, from, to, granularity }: {
  zoneId: string;
  from: string;
  to: string;
  granularity: Granularity;
}) {
  const { t } = useT();
  const { data: tab, error, isLoading } = useSWR(
    zoneId ? ['analytics:tab', zoneId, 'tables', from, to, granularity] : null,
    () => getZoneAnalyticsTab(zoneId, 'tables', from, to, granularity).then((r) => r.data),
    { revalidateOnFocus: false },
  );

  if (error && !tab) return <TabErrorCard error={error} />;

  const d = tab?.tables;

  if (d && d.total_tables === 0) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-36 text-muted-foreground text-sm">
          {t('analytics.tables.notConfigured')}
        </CardContent>
      </Card>
    );
  }

  // No full-tab skeleton: the strips card (first section) shows its own
  // loader, and stacking a second identical skeleton under it read as a bug.
  if (isLoading && !tab) {
    return (
      <div className="space-y-4">
        <TableStateStrips
          zoneId={zoneId}
          from={from}
          to={to}
          title={t('report.tables.stripsTitle')}
          experimental
          deepLink
        />
      </div>
    );
  }

  const tablesGrafanaUrl = tab?.grafana_dashboard_url;

  return (
    <div className="space-y-4">
      {/* KPI chips only when there is an actual cleanup stat — a row of «—»
          dashes told the manager nothing. No prev-period delta: Статистика
          no longer compares periods (the week REPORT carries the dynamics). */}
      {(d?.median_cleanup_s != null || d?.p90_cleanup_s != null) && (
        <KpiRow>
          <KpiChip
            label={t('analytics.tables.medianLabel')}
            value={fmtDuration(d?.median_cleanup_s ?? null)}
            accent
          />
          <KpiChip
            label={t('analytics.tables.p90Label')}
            value={fmtDuration(d?.p90_cleanup_s ?? null)}
          />
        </KpiRow>
      )}
      {d?.cleanup_histogram && (
        <CleanupHistogramBar
          hist={d.cleanup_histogram}
          label={t('analytics.tables.histLabel')}
        />
      )}
      {/* Сначала итог — «обычно убираем за» и распределение ожидания (Алексей 26.10 #2645
          «это надо наверх»), под ним полоски столов. */}
      <TableStateStrips
        zoneId={zoneId}
        from={from}
        to={to}
        title={t('report.tables.stripsTitle')}
        experimental
        // Модалка стола — в ссылке, «назад» закрывает её (Алексей 28.10 #3136/#3137).
        deepLink
      />
      {/* «Последние фото столов» убраны: фото столов — в полосках выше (Алексей 28.10 #3131). */}
      {tablesGrafanaUrl && (
        <div className="pt-2">
          <GrafanaButton url={tablesGrafanaUrl} label={t('analytics.grafana.openButton')} />
        </div>
      )}
    </div>
  );
}

// ── Tasks tab ─────────────────────────────────────────────────────────────────

/** «По сотрудникам» — who submitted what (Alexey 2026-08-11).
 *
 *  Counted on submitted REPORTS, not on task status: a task can flip to done
 *  without anyone submitting anything, and the question here is who did the
 *  work. «Закрыли» is the subset of submissions that actually closed a task, so
 *  five follow-up photos on one task can't read as five completed tasks.
 *
 *  Rows expand twice: employee → their tasks with counts → the actual executions
 *  and reports, reusing the very same ChecklistItemReports panel the
 *  by-task digest uses, so both routes into a report look identical. */
function ReporterRatingSection({
  items,
  zoneId,
  from,
  to,
}: {
  items: TaskReporterStat[];
  zoneId: string;
  from: string;
  to: string;
}) {
  const [openUser, setOpenUser] = useState<string | null>(null);
  const [openTask, setOpenTask] = useState<string | null>(null);
  if (!items.length) return null;

  const total = items.reduce((acc, r) => acc + r.submitted, 0);
  // Enough hues to keep neighbours distinguishable; recycled past the end, which
  // only bites on zones with 10+ active submitters in one period.
  const COLORS = [
    '#6366f1', '#22c55e', '#f59e0b', '#ec4899', '#06b6d4',
    '#a855f7', '#ef4444', '#84cc16', '#0ea5e9', '#f97316',
  ];
  const pie = items.map((r, i) => ({
    name: r.username,
    value: r.submitted,
    fill: COLORS[i % COLORS.length],
  }));

  return (
    <Card>
      <CardContent className="py-4">
        <div className="mb-3 flex items-center gap-2">
          <Users className="size-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">По сотрудникам</h3>
          <span className="text-xs text-muted-foreground">
            {total} отчётов за период
          </span>
        </div>

        <div className="mb-4 flex flex-col items-center gap-4 sm:flex-row">
          <div className="h-44 w-full max-w-[190px] shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pie} dataKey="value" nameKey="name" innerRadius={38} outerRadius={68} paddingAngle={1}>
                  {pie.map((p) => (
                    <Cell key={p.name} fill={p.fill} />
                  ))}
                </Pie>
                <Tooltip
                  isAnimationActive={false}
                  contentStyle={{
                    fontSize: 12,
                    borderRadius: 8,
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-card)',
                    color: 'var(--color-foreground)',
                  }}
                  formatter={(v, n) => [`${v} отчётов`, String(n)]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          {/* Legend is its own column, not recharts' <Legend>: the tg nicknames
              are long and the built-in legend truncates them into uselessness. */}
          <div className="grid w-full min-w-0 flex-1 gap-x-4 gap-y-1 sm:grid-cols-2">
            {pie.map((p, i) => (
              <button
                key={p.name}
                type="button"
                onClick={() => { setOpenUser(openUser === p.name ? null : p.name); setOpenTask(null); }}
                className="flex min-w-0 items-center gap-2 text-left text-xs hover:opacity-80"
              >
                <span className="size-2.5 shrink-0 rounded-sm" style={{ background: p.fill }} />
                <span className="truncate text-foreground">{p.name}</span>
                <span className="ml-auto shrink-0 tabular-nums text-muted-foreground">
                  {p.value}
                  {total > 0 && ` · ${Math.round((p.value / total) * 100)}%`}
                  {items[i].closed !== items[i].submitted && ` (${items[i].closed} закр.)`}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-1.5">
          {items.map((r) => {
            const isOpen = openUser === r.username;
            return (
              <div key={r.username} className="overflow-hidden rounded-lg border border-border">
                <button
                  type="button"
                  onClick={() => { setOpenUser(isOpen ? null : r.username); setOpenTask(null); }}
                  className={cn(
                    'flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-muted/40',
                    isOpen && 'bg-muted/40',
                  )}
                  aria-expanded={isOpen}
                >
                  <span className="flex min-w-0 items-center gap-1.5">
                    {isOpen
                      ? <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
                      : <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />}
                    <span className="truncate text-foreground">{r.username}</span>
                  </span>
                  <span className="shrink-0 text-xs font-semibold tabular-nums">
                    {r.submitted}
                    {r.closed !== r.submitted && (
                      <span className="ml-1 font-normal text-muted-foreground">
                        ({r.closed} закрыли)
                      </span>
                    )}
                  </span>
                </button>

                {isOpen && (
                  <div className="space-y-1.5 border-t border-border bg-muted/20 p-2">
                    {!r.tasks?.length && (
                      <div className="px-1 py-2 text-xs text-muted-foreground">
                        Разбивка по задачам недоступна — обновите бэкенд.
                      </div>
                    )}
                    {r.tasks?.map((tk) => {
                      const key = `${r.username}::${tk.task_text}`;
                      const taskOpen = openTask === key;
                      return (
                        <div key={key} className="overflow-hidden rounded-md border border-border bg-card">
                          <button
                            type="button"
                            onClick={() => setOpenTask(taskOpen ? null : key)}
                            className={cn(
                              'flex w-full items-center justify-between gap-3 px-2.5 py-1.5 text-left text-xs transition-colors hover:bg-muted/40',
                              taskOpen && 'bg-muted/40',
                            )}
                            aria-expanded={taskOpen}
                          >
                            <span className="flex min-w-0 items-center gap-1.5">
                              {taskOpen
                                ? <ChevronDown className="size-3 shrink-0 text-muted-foreground" />
                                : <ChevronRight className="size-3 shrink-0 text-muted-foreground" />}
                              <span className="truncate text-foreground">{tk.task_text}</span>
                            </span>
                            <span className="shrink-0 tabular-nums text-muted-foreground">
                              {tk.submitted}&times;
                            </span>
                          </button>
                          {taskOpen && tk.checklist_item_ids.length > 0 && (
                            <ChecklistItemReports
                              zoneId={zoneId}
                              checklistItemIds={tk.checklist_item_ids}
                              from={from}
                              to={to}
                            />
                          )}
                          {taskOpen && tk.checklist_item_ids.length === 0 && (
                            <div className="px-3 py-2 text-xs text-muted-foreground">
                              Эта задача не привязана к чек-листу — показать её отчёты нечем.
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function TasksTab({
  zoneId,
  granularity,
  from,
  to,
}: {
  zoneId: string;
  granularity: Granularity;
  from: string;
  to: string;
}) {
  const { t } = useT();
  const { data: tab, error, isLoading } = useSWR(
    zoneId ? ['analytics:tab', zoneId, 'tasks', from, to, granularity] : null,
    () => getZoneAnalyticsTab(zoneId, 'tasks', from, to, granularity).then((r) => r.data),
    { revalidateOnFocus: false },
  );

  if (isLoading) return <TabLoadingSkeleton />;
  if (error && !tab) return <TabErrorCard error={error} />;

  const d = tab?.tasks;

  const chartData = (d?.timeseries ?? []).map((row) => ({
    date: row.date,
    value: row.rate,
  }));

  const textSummary = d != null && d.total > 0
    ? `${d.done} / ${d.total} задач выполнено`
    : undefined;

  if (d && d.total === 0) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-36 text-muted-foreground text-sm">
          {t('analytics.tasks.noData')}
        </CardContent>
      </Card>
    );
  }

  const tasksGrafanaUrl = tab?.grafana_dashboard_url;

  return (
    <div className="space-y-4">
      <KpiRow>
        <KpiChip
          label={t('analytics.tasks.statLabel')}
          value={d?.completion_rate != null ? `${d.completion_rate}%` : '—'}
          delta={d?.delta_pct ?? null}
          deltaUnit="%"
          higherIsBetter={true}
          accent
        />
        <KpiChip
          label={t('analytics.tasks.onTimeLabel')}
          value={d?.on_time_rate != null ? `${d.on_time_rate}%` : '—'}
          delta={d?.on_time_delta_pct ?? null}
          deltaUnit="%"
          higherIsBetter={true}
        />
        <KpiChip
          label={t('analytics.tasks.completedLabel')}
          value={d != null ? String(d.done) : '—'}
        />
        <KpiChip
          label={t('analytics.tasks.totalLabel')}
          value={d != null ? String(d.total) : '—'}
        />
      </KpiRow>
      <Sparkline
        data={chartData}
        granularity={granularity}
        color="#22c55e"
        label={t('analytics.tasks.chartLabel')}
        unit="%"
        textSummary={textSummary}
      />
      {d && (d.timing_histogram || d.total > d.done) && (
        <TimingHistogramBar
          hist={d.timing_histogram}
          notDone={Math.max(0, d.total - d.done)}
          label={t('analytics.tasks.timingLabel')}
        />
      )}
      <ChecklistDigestSection
        items={d?.chronic_fail ?? []}
        title={t('analytics.tasks.chronicFail.title')}
        empty={t('analytics.tasks.chronicFail.empty')}
        icon={AlertTriangle}
        iconClassName="text-orange-500"
        rateClass={failRateClass}
        zoneId={zoneId}
        from={from}
        to={to}
      />
      <ChecklistDigestSection
        items={d?.top_done ?? []}
        title={t('analytics.tasks.topDone.title')}
        empty={t('analytics.tasks.topDone.empty')}
        icon={CheckCircle2}
        iconClassName="text-green-600 dark:text-green-400"
        rateClass={doneRateClass}
        zoneId={zoneId}
        from={from}
        to={to}
      />
      <ReporterRatingSection items={d?.by_reporter ?? []} zoneId={zoneId} from={from} to={to} />
      {tasksGrafanaUrl && (
        <div className="pt-2">
          <GrafanaButton url={tasksGrafanaUrl} label={t('analytics.grafana.openButton')} />
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

/** Вкладки, где события редкие: там нужен длинный быстрый период. */
// Рекомендованный период вкладки: разделы с редкими событиями — 3 месяца,
// остальные — неделя. Если первые данные филиала моложе, рекомендуем «Все
// данные» с даты первых данных: на дыры не отвлекаемся, только genuinely
// first data ever (Алексей 17.09).
// Недопродажи — 7 дней (Алексей 29.10 #3257); было 14, а подпись говорила «7 дней» (#3256).
const RECOMMENDED_DAYS: Record<string, number> = { reviews: 90, visits: 90, receipts: 30, item_sales: 7 };
const DEFAULT_RECOMMENDED_DAYS = 7;
const FIRST_DATA_KEY: Record<string, DataSection> = {
  layout: 'monitoring', queue: 'monitoring', tables: 'monitoring', heatmap: 'monitoring',
  tasks: 'checklists', conversations: 'speech', visits: 'visits', reviews: 'reviews', wifi: 'wifi',
};

/** Мобильная навигация по вкладкам — стеклянная полоска снизу, как в дашборде
 *  смены, но с горизонтальной прокруткой: видеть все вкладки разом тут не
 *  нужно (Алексей 17.09). Свою высоту сообщает наверх, чтобы контент получил
 *  нижний отступ и последний блок можно было долистать из-под полоски. */
/** Телефон: вкладки и период в полосе второго уровня, как в Отчёте (Алексей
 *  17.09, AI12.9). Вкладки листаются по горизонтали внутри своей части
 *  полосы, период и «+» стоят справа неподвижно. */
function StatsMobileBar({ tabs, active, onSelect, label, trailing }: {
  tabs: { value: string; label: string; icon: LucideIcon }[];
  active: string;
  onSelect: (v: string) => void;
  label: string;
  trailing: React.ReactNode;
}) {
  const activeRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [active]);
  return (
    <MobileSubHeader className="gap-1 pr-1">
      <nav
        aria-label={label}
        className="flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {tabs.map((tb) => {
          const isActive = tb.value === active;
          const Icon = tb.icon;
          return (
            <button
              key={tb.value}
              ref={isActive ? activeRef : undefined}
              type="button"
              onClick={() => onSelect(tb.value)}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                'flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-[13px] font-medium transition-colors',
                isActive ? 'bg-foreground/10 text-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Icon className="size-3.5" />
              {tb.label}
            </button>
          );
        })}
      </nav>
      <div className="flex shrink-0 items-center gap-0.5 border-l border-border pl-1">{trailing}</div>
    </MobileSubHeader>
  );
}

export default function AnalyticsPage() {
  const { t, lang } = useT();
  usePageTitle(t('analytics.title'), t('analytics.subtitle'));
  const locale = lang === 'en' ? 'en-US' : 'ru-RU';

  const { currentZone } = useCurrentZone();
  const zoneId = currentZone?.id ? String(currentZone.id) : '';


  // Arbitrary day range, owned by the page header: preset chips (7/30 days)
  // plus explicit from/to pickers. Defaults to TODAY (Alexey 2026-07-21) — a
  // single-day range the backend clips to the branch's working hours (±30 min
  // pad), so the page opens on today's working window rather than the last week
  // or a 00:00-anchored day.
  // Period lives in the URL alongside ?tab= so a stats link carries BOTH what
  // you were looking at and when (Alexey 2026-08-11: «чтобы как и везде, в ссылку
  // на статистику зашивался период и выбранная вкладка»). Falls back to today,
  // which is what the page opened on before.
  const [searchParams, setSearchParams] = useSearchParams();
  // Период и вкладка переживают уход через сайдбар (п. 73/74).
  useRememberFilters(['from', 'to', 'tab']);
  const from = searchParams.get('from') || todayIso();
  const to = searchParams.get('to') || todayIso();
  // ?rack=<id> from an empty-shelf alert deep-link → pre-selects that stellage on
  // the выкладка tab (Alexey 2026-08-24). Non-numeric/absent → null (no preselect).
  const rackParamRaw = searchParams.get('rack');
  const rackParam =
    rackParamRaw === 'all' ? -1 : rackParamRaw && /^\d+$/.test(rackParamRaw) ? Number(rackParamRaw) : null;
  const timeParam = searchParams.get('time');
  const timeWindow = parseTimeWindow(from, to, timeParam);
  // Period changes PUSH a history entry (not replace), so the browser's Back
  // returns to the previous period / chart window / rack (Alexey #434).
  const setPeriod = (nextFrom: string, nextTo: string, nextTime: string | null = null) => {
    if (nextFrom === from && nextTo === to && nextTime === (timeWindow ? timeParam : null)) return;
    // Functional updater, NOT the closed-over `searchParams`: after the user
    // switches branch, BranchSelector has just rewritten ?zone= but this
    // callback's `searchParams` can still be the pre-switch snapshot, so
    // copying it here re-published the OLD zone and the URL→context reconcile
    // snapped the branch back to the previous one (Alexey 2026-08-21: day/week
    // «сбрасывает на предыдущий филиал»). `prev` is always the live params.
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      // Today is the default — keep it out of the URL so the common link stays short.
      if (nextFrom === todayIso() && nextTo === todayIso()) {
        p.delete('from');
        p.delete('to');
      } else {
        p.set('from', nextFrom);
        p.set('to', nextTo);
      }
      if (nextTime) p.set('time', nextTime);
      else p.delete('time');
      return p;
    });
  };
  // Marquee over the выкладка chart = a NEW period (dates + ?time=), not a
  // local zoom with a «Сброс» button (#434). null → the whole days again.
  const setTimeWindow = (w: [number, number] | null) => {
    if (!w) {
      setPeriod(from, to, null);
      return;
    }
    const a = new Date(w[0]);
    const b = new Date(w[1]);
    const hhmm = (d: Date) => `${pad2(d.getHours())}${pad2(d.getMinutes())}`;
    const time = `${hhmm(a)}-${hhmm(b)}`;
    setPeriod(isoOf(a), isoOf(b), time === '0000-2359' ? null : time);
  };
  const setRack = (id: number) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('rack', id === -1 ? 'all' : String(id));
      return p;
    });
  };

  // «Добавить вкладки» promo dropdown (only when something CAN be added).
  const [addTabsOpen, setAddTabsOpen] = useState(false);
  const addTabsRef = useRef<HTMLButtonElement | null>(null);

  // Tab lives in the URL (?tab=) so the old /usage/heatmap link can land on
  // the Тепловая карта tab and tab links are shareable.
  const tab = searchParams.get('tab') ?? 'layout';
  const setTab = (next: string) => {
    // Same stale-snapshot guard as setPeriod: use the live params so switching
    // tabs never republishes an out-of-date ?zone=.
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next === 'layout') p.delete('tab');
      else p.set('tab', next);
      // Выбран рекомендованный период (★) — на другой вкладке остаётся ★ ЕЁ периода,
      // а не те же абсолютные даты (Алексей 12.10, #1440): у отзывов и визитов 90 дней,
      // у мониторинга 7 — иначе переход в отзывы давал пустую неделю.
      const curFrom = p.get('from') || todayIso(); const curTo = p.get('to') || todayIso();
      const curRec = recommendedFor(tab);
      if (curFrom === curRec.from && curTo === curRec.to) {
        const nextRec = recommendedFor(next);
        p.set('from', nextRec.from); p.set('to', nextRec.to);
      }
      return p;
    }, { replace: true });
  };

  // 30 daily points still fit comfortably; week-buckets collapsed a month
  // into ~4 points, hiding all variation.
  const granularity: Granularity = 'day';

  // Monday of the most recent COMPLETED week (for the «Прошлая неделя» chip).
  const prevWeek = (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    const sinceMon = d.getDay() === 0 ? 6 : d.getDay() - 1;
    d.setDate(d.getDate() - sinceMon - 7);
    const mon = isoOf(d);
    d.setDate(d.getDate() + 6);
    return { from: mon, to: isoOf(d) };
  })();

  // С какого дня у филиала есть данные — для рекомендации «Все данные».
  const { data: firstData } = useSWR(
    zoneFirstDataKey(zoneId),
    () => getZoneFirstData(zoneId),
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  const recommendedFor = (tabKey: string) => {
    const days = RECOMMENDED_DAYS[tabKey] ?? DEFAULT_RECOMMENDED_DAYS;
    const first = firstData?.[FIRST_DATA_KEY[tabKey] ?? 'monitoring']?.first ?? null;
    if (first && first > daysAgoIso(days)) {
      return { label: t('analytics.period.allData'), from: first, to: todayIso(), recommended: true };
    }
    return {
      label: days === 90 ? t('analytics.period.3m') : days === 30 ? t('analytics.period.30d') : days === 7 ? t('analytics.period.7d') : `${days} дн.`,
      from: daysAgoIso(days), to: todayIso(), recommended: true,
    };
  };
  const recommended = recommendedFor(tab);
  const isRecommended = from === recommended.from && to === recommended.to;
  const basePresets = [
    { label: t('analytics.period.today'), from: todayIso(), to: todayIso() },
    { label: t('analytics.period.yesterday'), from: daysAgoIso(1), to: daysAgoIso(1) },
    { label: t('analytics.period.7d'), from: daysAgoIso(7), to: todayIso() },
    { label: t('analytics.period.prevWeek'), from: prevWeek.from, to: prevWeek.to },
    // Разделы с редкими событиями (отзывы, визиты) без длинного периода почти
    // всегда пустые, и пустой экран выглядел как поломка. Рекомендованный период
    // должен быть прямо в быстрых вариантах, а не только в кнопке пустого стейта
    // (Алексей 16.09); помечен звёздочкой (17.09).
    ...(RECOMMENDED_DAYS[tab] === 90 ? [{ label: t('analytics.period.3m'), from: daysAgoIso(90), to: todayIso() }] : []),
  ];
  const sameRange = (p: { from: string; to: string }) => p.from === recommended.from && p.to === recommended.to;
  const presets = basePresets.some(sameRange)
    ? basePresets.map((p) => (sameRange(p) ? { ...p, recommended: true } : p))
    : [...basePresets, recommended];
  // Если выбран не рекомендованный период — звёздочка рядом с пикером, по клику
  // ставит его; тултип чёрный и быстрый. Слева такой же невидимый спейсер, чтобы
  // пикер не съезжал и оставался по центру шапки (Алексей 17.09).
  const recommendButton = !isRecommended ? (
    <UiTooltip content={t('analytics.period.recommendedTip', { label: recommended.label })} openDelay={0} side="bottom">
      <button
        type="button"
        onClick={() => setPeriod(recommended.from, recommended.to)}
        aria-label={t('analytics.period.recommendedTip', { label: recommended.label })}
        className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-primary transition-colors hover:bg-primary/10"
      >
        <Star className="size-4" />
      </button>
    </UiTooltip>
  ) : null;
  const picker = (compact: boolean) => (
    <DateRangePicker
      from={from}
      to={to}
      max={todayIso()}
      onChange={(f, tto) => setPeriod(f, tto)}
      // Выделенный на графике подпериод — одной человеческой строкой (#5805).
      labelOverride={timeWindow ? fmtPeriod(timeWindow[0], timeWindow[1]) : undefined}
      locale={locale}
      ariaLabel={t('analytics.period.from')}
      triggerClassName="h-8"
      presets={presets}
      compact={compact}
    />
  );
  const periodControls = (
    <div className="flex items-center gap-1.5">
      {recommendButton && <span className="invisible size-8 shrink-0" aria-hidden />}
      {picker(false)}
      {recommendButton}
    </div>
  );
  // На телефоне период — иконка в самой шапке, рядом с филиалом (п. 4 от 17.09).
  const periodControlsMobile = (
    <div className="flex items-center gap-0.5">
      {picker(true)}
      {recommendButton}
    </div>
  );

  // Desktop: the period lives in the top bar (like the report pages); the
  // in-page copy below covers mobile, where the header slot is hidden.
  // The WHOLE query string is a dep, not just from/to: react-router v6's
  // functional setSearchParams hands `prev` from the render the callback was
  // created in, so a header slot kept from before a tab switch rewrote the URL
  // without ?tab= and every date change snapped back to Выкладка (Alexey 15.09).
  // Шестерёнка у заголовка на телефоне ведёт в настройки функции вкладки.
  const SETTINGS_BY_TAB: Record<string, string> = {
    layout: '/management/zoning', queue: '/management/zoning', tables: '/management/zoning', heatmap: '/management/zoning',
    tasks: '/management/checklists', conversations: '/management/speech', visits: '/management/visits', reviews: '/management/reviews',
  };

  // Which tabs exist here comes from the «Подключенные функции» registry (M1):
  // a tab shows once its function is on AND has something to show.
  const { features } = useZoneFeatures(zoneId);
  const tasksConfigured = !!(features?.checklists?.enabled && features.checklists.has_content);
  const visitsConfigured = !!(features?.visits?.enabled && features.visits.has_content);
  const reviewsConfigured = !!features?.reviews?.enabled;
  // Чеки (#2364): вкладка, когда кассы филиала подключены и чеки уже есть.
  const receiptsConfigured = !!(features?.receipts?.enabled && features.receipts.has_content);
  // Речь appears with a microphone, the way zone monitoring appears with a camera.
  const hasMic = !!features?.speech?.enabled;
  // Вкладка — сразу по включению, не дожидаясь первых записей: иначе «включил, а
  // вкладки нет» (Алексей 17.09, AI29); пустое состояние вкладка рисует сама.
  const wifiConfigured = !!features?.wifi?.enabled;
  // «В план заказа» (#2972; открыто 05.11 #4459, собрание всего «чего не хватило» — #4464): не отдельная функция.
  // Среди источников — выкладка, а она есть у каждой точки (как вкладка «Выкладка»), поэтому вкладка — всегда;
  // неподключённые звук/отзывы/чеки/визиты вкладка показывает строкой «Источники» и советами.
  const orderPlanShown = true;
  // «Добавить вкладки» берёт список из ТОГО ЖЕ реестра, что и «Подключенные
  // функции» в общих настройках: раньше здесь была захардкожена одна строка про
  // чек-листы, списки расходились, и часть вкладок было «нельзя добавить»
  // (Алексей 16.09, п. 28). Предлагаем каждую функцию, чьей вкладки сейчас нет,
  // и ведём на её страницу настройки — ту же, что и кнопка «Настроить».
  const addableTabs: { key: string; icon: LucideIcon; label: string; to: string }[] = ([
    { key: 'conversations', shown: hasMic, icon: MessageSquareText, label: t('features.speech.name'), to: '/management/speech' },
    { key: 'tasks', shown: tasksConfigured, icon: ClipboardList, label: t('features.checklists.name'), to: '/management/checklists' },
    { key: 'visits', shown: visitsConfigured, icon: ClipboardCheck, label: t('features.visits.name'), to: '/management/visits' },
    { key: 'reviews', shown: reviewsConfigured, icon: Star, label: t('features.reviews.name'), to: '/management/reviews' },
    { key: 'receipts', shown: receiptsConfigured, icon: Receipt, label: t('features.receipts.name'), to: '/management/receipts' },
    { key: 'wifi', shown: wifiConfigured, icon: Wifi, label: t('features.wifi.name'), to: '/management/equipment' },
  ]).filter((x) => !x.shown);
  const zoneSearch = searchParams.get('zone') ? `?zone=${searchParams.get('zone')}` : '';
  // Сбрасываем вкладку, только когда реестр функций УЖЕ отвечал: пока он
  // грузится, «микрофона нет» неотличимо от «ещё не знаем», и ссылка на
  // ?tab=conversations открывалась на «Выкладке», а смена даты выбрасывала с
  // вкладки речи (владелец, 16.09 AB11: «the tab flag should always be set
  // immediately»).
  useEffect(() => {
    if (!features) return;
    if (
      (tab === 'tasks' && !tasksConfigured)
      || (tab === 'conversations' && !hasMic)
      || (tab === 'visits' && !visitsConfigured)
      || (tab === 'reviews' && !reviewsConfigured)
      || (tab === 'receipts' && !receiptsConfigured)
      || (tab === 'item_sales' && !receiptsConfigured)
      || (tab === 'wifi' && !wifiConfigured)
    ) {
      setTab('layout');
    }
  }, [tab, features, tasksConfigured, hasMic, visitsConfigured, reviewsConfigured, receiptsConfigured, wifiConfigured]);

  const isMobile = useMediaQuery('(max-width: 767px)');
  // На телефоне «Добавить вкладки» — не в полосе вкладок, а в шестерёнке у
  // заголовка (Алексей 17.09, AI17.6).
  const addTabsExtra = isMobile && features && addableTabs.length > 0 ? (
    <div className="space-y-0.5">
      <div className="pb-1 text-[10px] uppercase tracking-wider text-muted-foreground">{t('analytics.addTabs')}</div>
      {addableTabs.map(({ key, icon: Icon, label, to }) => (
        <Link
          key={key}
          to={{ pathname: to, search: zoneSearch }}
          className="-mx-1 flex items-center gap-2 rounded-md px-1 py-1.5 text-sm text-foreground hover:bg-muted"
        >
          <Icon className="size-4 text-muted-foreground" />
          <span className="flex-1">{label}</span>
          <span className="text-xs text-muted-foreground">{t('dashboard.teaser.configure')}</span>
        </Link>
      ))}
    </div>
  ) : null;
  usePageHeaderSlots(
    { center: periodControls, settingsTo: SETTINGS_BY_TAB[tab] ?? null, extra: addTabsExtra },
    [searchParams.toString(), lang, tab, recommended.from, recommended.label, isMobile, addableTabs.map((x) => x.key).join()],
  );
  const mobileTabs: { value: string; label: string; icon: LucideIcon }[] = [
    { value: 'layout', label: t('analytics.tab.layout'), icon: BarChart3 },
    { value: 'queue', label: t('analytics.tab.queue'), icon: ShoppingCart },
    { value: 'tables', label: t('analytics.tab.tables'), icon: UtensilsCrossed },
    ...(tasksConfigured ? [{ value: 'tasks', label: t('analytics.tab.tasks'), icon: ClipboardList }] : []),
    ...(hasMic ? [{ value: 'conversations', label: t('analytics.tab.conversations'), icon: MessageSquareText }] : []),
    ...(visitsConfigured ? [{ value: 'visits', label: t('visits.liveTitle'), icon: Eye }] : []),
    ...(reviewsConfigured ? [{ value: 'reviews', label: t('features.reviews.name'), icon: Star }] : []),
    ...(receiptsConfigured ? [{ value: 'receipts', label: t('features.receipts.name'), icon: Receipt }] : []),
    ...(receiptsConfigured ? [{ value: 'item_sales', label: 'Недопродажи', icon: Clock }] : []),
    ...(orderPlanShown ? [{ value: 'order_plan', label: t('analytics.tab.orderPlan'), icon: PackageSearch }] : []),
    ...(wifiConfigured ? [{ value: 'wifi', label: 'Wi-Fi', icon: Wifi }] : []),
    { value: 'heatmap', label: t('sidebar.heatmap'), icon: Flame },
  ];

  // Per-tab fetching: each tab component runs its own SWR keyed by tab name.
  // Only the active tab is mounted (TabsContent returns null for inactive),
  // so non-visited tabs cost zero requests. SWR's natural promise-sharing
  // dedups in-flight requests across tab toggles within the same key.

  // No-access (?zone= outside the user's list) is handled inside
  // BranchRequiredState so every gated page says the same thing.
  const addTabsButton = features && addableTabs.length > 0 ? (
    <button
      ref={addTabsRef}
      type="button"
      onClick={() => setAddTabsOpen((v) => !v)}
      className="inline-flex h-8 shrink-0 items-center gap-1 rounded-full border border-dashed border-border px-2 text-xs font-medium text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground md:px-3"
      aria-label={t('analytics.addTabs')}
    >
      <Plus className="size-3.5" />
      <span className="hidden md:inline">{t('analytics.addTabs')}</span>
    </button>
  ) : null;

  if (!zoneId) {
    return <BranchRequiredState />;
  }

  return (
    // Exactly one screen, the way the Зонирование tab does it: h-full pins us
    // to <main>'s height (main is overflow-auto and NOT a flex container, so
    // flex-1/min-h-0 can't propagate — height:100% can), overflow-hidden means
    // the PAGE never scrolls. Tabs whose content is naturally taller scroll
    // inside their own panel instead, so the period picker and the tab bar
    // stay put on every tab. Before this the page was neither: short tabs
    // underfilled the screen, tall ones pushed the whole document into scroll.
    // На телефоне подложка белая (bg-card): секции без рамок на серой читались
    // как «пропали карточки» (Алексей 17.09); нижний отступ — под стеклянную
    // полоску вкладок, чтобы последний блок долистывался из-под неё.
    // Телефон: полоса второго уровня сверху, отступы — у прокручиваемых
    // панелей вкладок (карточки-секции выходят в ширину экрана внутри них).
    <div className="m-bleed flex h-full flex-col overflow-hidden md:p-4">
      {isMobile && (
        <StatsMobileBar
          tabs={mobileTabs}
          active={tab}
          onSelect={setTab}
          label={t('analytics.tabsNav')}
          trailing={periodControlsMobile}
        />
      )}

      {/* Tabs. Desktop: the usual pill list; mobile: the standard dropdown
          (the pill row wrapped to 2-3 lines on phones). The «Добавить
          вкладки» promo sits right-aligned in the same row. */}
      <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col">
        <div className="flex shrink-0 items-center justify-between gap-2 max-md:hidden">
          <div className="min-w-0">
            <TabsList>
              <TabsTrigger value="layout">
                <BarChart3 className="size-3.5" />
                {t('analytics.tab.layout')}
              </TabsTrigger>
              <TabsTrigger value="queue">
                <ShoppingCart className="size-3.5" />
                {t('analytics.tab.queue')}
              </TabsTrigger>
              <TabsTrigger value="tables">
                <UtensilsCrossed className="size-3.5" />
                {t('analytics.tab.tables')}
              </TabsTrigger>
              {tasksConfigured && (
                <TabsTrigger value="tasks">
                  <ClipboardList className="size-3.5" />
                  {t('analytics.tab.tasks')}
                </TabsTrigger>
              )}
              {hasMic && (
                <TabsTrigger value="conversations">
                  <MessageSquareText className="size-3.5" />
                  {t('analytics.tab.conversations')}
                </TabsTrigger>
              )}
              {visitsConfigured && (
                <TabsTrigger value="visits">
                  <Eye className="size-3.5" />
                  {t('visits.liveTitle')}
                </TabsTrigger>
              )}
              {reviewsConfigured && (
                <TabsTrigger value="reviews">
                  <Star className="size-3.5" />
                  {t('features.reviews.name')}
                </TabsTrigger>
              )}
              {receiptsConfigured && (
                <TabsTrigger value="receipts">
                  <Receipt className="size-3.5" />
                  {t('features.receipts.name')}
                </TabsTrigger>
              )}
              {receiptsConfigured && (
                <TabsTrigger value="item_sales">
                  <Clock className="size-3.5" />
                  Недопродажи
                </TabsTrigger>
              )}
              {orderPlanShown && (
                <TabsTrigger value="order_plan">
                  <PackageSearch className="size-3.5" />
                  {t('analytics.tab.orderPlan')}
                </TabsTrigger>
              )}
              {wifiConfigured && (
                <TabsTrigger value="wifi">
                  <Wifi className="size-3.5" />
                  Wi-Fi
                </TabsTrigger>
              )}
              <TabsTrigger value="heatmap">
                <Flame className="size-3.5" />
                {t('sidebar.heatmap')}
              </TabsTrigger>
            </TabsList>
          </div>
          {!isMobile && addTabsButton}
        </div>
          {features && addableTabs.length > 0 && (
            <>
              <Popover
                open={addTabsOpen}
                onOpenChange={setAddTabsOpen}
                triggerRef={addTabsRef}
                align="end"
                className="w-64 rounded-lg border border-border bg-card p-2 shadow-md"
              >
                <div className="mb-1.5 px-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                  {t('analytics.couldAppear')}
                </div>
                {addableTabs.map(({ key, icon: Icon, label, to }) => (
                  <Link
                    key={key}
                    to={{ pathname: to, search: zoneSearch }}
                    onClick={() => setAddTabsOpen(false)}
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground hover:bg-muted"
                  >
                    <Icon className="size-4 text-muted-foreground" />
                    <span className="flex-1">{label}</span>
                    <span className="text-xs text-muted-foreground">{t('dashboard.teaser.configure')}</span>
                  </Link>
                ))}
              </Popover>
            </>
          )}

        {/* Выкладка fills the panel exactly and owns its own (rare) scrolling;
            every other tab is a stack of cards that legitimately runs long, so
            it scrolls WITHIN the panel. Either way the page itself never
            scrolls and never leaves the screen half-empty. */}
        <TabsContent value="layout" className="mt-4 min-h-0 flex-1 overflow-hidden max-md:mt-0 max-md:overflow-y-auto max-md:p-4">
          <AnalyticsLayoutDetailTab
            zoneId={zoneId}
            from={from}
            to={to}
            rackId={rackParam}
            onRackChange={setRack}
            timeWindow={timeWindow}
            onTimeWindow={setTimeWindow}
          />
        </TabsContent>
        <TabsContent value="queue" className="mt-4 min-h-0 flex-1 overflow-y-auto max-md:mt-0 max-md:p-4">
          <AnalyticsQueueDetailTab zoneId={zoneId} from={from} to={to} timeWindow={timeWindow} onTimeWindow={setTimeWindow} />
        </TabsContent>
        <TabsContent value="tables" className="mt-4 min-h-0 flex-1 space-y-6 overflow-y-auto max-md:mt-0 max-md:p-4">
          <TablesTab zoneId={zoneId} from={from} to={to} granularity={granularity} />
        </TabsContent>
        {tasksConfigured && (
          <TabsContent value="tasks" className="mt-4 min-h-0 flex-1 overflow-y-auto max-md:mt-0 max-md:p-4">
            <TasksTab zoneId={zoneId} from={from} to={to} granularity={granularity} />
          </TabsContent>
        )}
        {hasMic && (
          <TabsContent value="conversations" className="mt-4 min-h-0 flex-1 overflow-y-auto max-md:mt-0 max-md:p-4">
            <AnalyticsConversationsTab zoneId={zoneId} from={from} to={to} granularity={granularity} />
          </TabsContent>
        )}
        {visitsConfigured && (
          <TabsContent value="visits" className="mt-4 min-h-0 flex-1 overflow-y-auto max-md:mt-0 max-md:p-4">
            <AnalyticsVisitsTab zoneId={zoneId} from={from} to={to} />
          </TabsContent>
        )}
        {receiptsConfigured && (
          <TabsContent value="receipts" className="mt-4 min-h-0 flex-1 overflow-y-auto max-md:mt-0 max-md:p-4">
            <AnalyticsReceiptsTab zoneId={zoneId} from={from} to={to} />
          </TabsContent>
        )}
        {receiptsConfigured && (
          <TabsContent value="item_sales" className="mt-4 min-h-0 flex-1 overflow-y-auto max-md:mt-0 max-md:p-4">
            <AnalyticsItemSalesTab zoneId={zoneId} from={from} to={to} />
          </TabsContent>
        )}
        {orderPlanShown && (
          <TabsContent value="order_plan" className="mt-4 min-h-0 flex-1 overflow-y-auto max-md:mt-0 max-md:p-4">
            <OrderPlanTab zoneId={zoneId} from={from} to={to} />
          </TabsContent>
        )}
        {reviewsConfigured && (
          <TabsContent value="reviews" className="mt-4 min-h-0 flex-1 overflow-y-auto max-md:mt-0 max-md:p-4">
            <AnalyticsReviewsTab zoneId={zoneId} from={from} to={to} />
          </TabsContent>
        )}
        {wifiConfigured && (
          <TabsContent value="wifi" className="mt-4 min-h-0 flex-1 overflow-y-auto max-md:mt-0 max-md:p-4">
            <AnalyticsWifiTab zoneId={zoneId} from={from} to={to} />
          </TabsContent>
        )}
        <TabsContent value="heatmap" className="mt-4 min-h-0 flex-1 overflow-y-auto max-md:mt-0 max-md:p-4">
          <HeatmapTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
